if (!customElements.get('next-gen-collage-showcase')) {
  class NextGenCollageShowcase extends HTMLElement {
    constructor() {
      super();

      // Config
      this.sectionId = '';
      this.enableStickyDesktop = false;
      this.enableStickyMobile = false;
      this.productsCount = 0;
      this.scrollMultiplier = 3;
      this.smoothing = 0.12;
      this.activeOpacity = 1;
      this.inactiveOpacity = 0.3;
      this.activeScale = 1.02;
      this.inactiveScale = 0.97;
      this.showArrows = true;
      this.enableFade = true;
      this.parallaxStrength = 0;
      this.quickAddMode = 'none';

      // Scroll state
      this.stickyStart = 0;
      this.stickyEnd = 0;
      this.scrollDistance = 0;
      this.progress = 0; // 0..1
      this.isInStickyRange = false;

      // Slider state
      this.currentIndex = 0;
      this.maxIndex = 0;
      this.targetTranslate = 0;
      this.currentTranslate = 0;
      this.minTranslate = 0;
      this.maxTranslate = 0;
      this.cardStep = 0;

      // Drag state
      this.isDragging = false;
      this.wasDragged = false;
      this.startX = 0;
      this.prevTranslate = 0;
      this.velocityX = 0;
      this.lastX = 0;
      this.lastTime = 0;

      // Bound handlers
      this._onScroll = this._onScroll.bind(this);
      this._onDragStart = this._onDragStart.bind(this);
      this._onDragMove = this._onDragMove.bind(this);
      this._onDragEnd = this._onDragEnd.bind(this);
      this._onResize = this._debounce(this._onResize.bind(this), 150);
      this._animationLoop = this._animationLoop.bind(this);
      this._onPrevClick = this._onPrevClick.bind(this);
      this._onNextClick = this._onNextClick.bind(this);
      this._onKeyDown = this._onKeyDown.bind(this);
      this._onSectionLoad = this._onSectionLoad.bind(this);
      this._onMotionChange = this._onMotionChange.bind(this);

      this._reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    }

    /* ─── LIFECYCLE ─── */

    connectedCallback() {
      this._readConfig();
      this._cacheDOM();
      if (!this.track || this.cards.length === 0) return;

      this._calculateLayout();
      this._bindEvents();
      this._createDots();
      this._updateCardStates(0);
      this._updateArrowStates();

      // Intro animation
      if (this.dataset.enableIntro === 'true' && !this._reducedMotion.matches) {
        this._setupIntro();
      }

      // Start render loop
      this._rafId = requestAnimationFrame(this._animationLoop);

      // Shopify theme editor
      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:section:load', this._onSectionLoad);
      }
    }

    disconnectedCallback() {
      this._unbindEvents();
      cancelAnimationFrame(this._rafId);
      if (this._resizeObserver) this._resizeObserver.disconnect();
      if (window.Shopify && window.Shopify.designMode) {
        document.removeEventListener('shopify:section:load', this._onSectionLoad);
      }
    }

    /* ─── CONFIG ─── */

    _readConfig() {
      this.sectionId = this.dataset.sectionId || '';
      this.enableStickyDesktop = this.dataset.enableStickyDesktop === 'true';
      this.enableStickyMobile = this.dataset.enableStickyMobile === 'true';
      this.productsCount = parseInt(this.dataset.productsCount, 10) || 0;
      this.scrollMultiplier = parseFloat(this.dataset.scrollMultiplier) || 3;
      // Values are already divided by 100 in Liquid (e.g. 100% → 1.0, 30% → 0.3)
      this.smoothing = parseFloat(this.dataset.smoothing) || 0.12;
      this.activeOpacity = parseFloat(this.dataset.activeOpacity) || 1;
      this.inactiveOpacity = parseFloat(this.dataset.inactiveOpacity) || 0.3;
      this.activeScale = parseFloat(this.dataset.activeScale) || 1.02;
      this.inactiveScale = parseFloat(this.dataset.inactiveScale) || 0.97;
      this.showArrows = this.dataset.showArrows === 'true';
      this.enableFade = this.dataset.enableFade === 'true';
      this.parallaxStrength = parseInt(this.dataset.parallaxStrength, 10) || 0;
      this.quickAddMode = this.dataset.quickAdd || 'none';
    }

    _cacheDOM() {
      this.pinWrapper = this.querySelector('.ng-collage-showcase__pin-wrapper');
      this.layout = this.querySelector('.ng-collage-showcase__layout');
      this.imageCol = this.querySelector('.ng-collage-showcase__image-col');
      this.imageWrapper = this.querySelector('.ng-collage-showcase__image-wrapper');
      this.imageEl = this.querySelector('.ng-collage-showcase__image');
      this.contentCol = this.querySelector('.ng-collage-showcase__content-col');
      this.sliderArea = this.querySelector('.ng-collage-showcase__slider-area');
      this.viewport = this.querySelector('.ng-collage-showcase__slider-viewport');
      this.track = this.querySelector('.ng-collage-showcase__track');
      this.cards = Array.from(this.querySelectorAll('.ng-collage-showcase__card'));
      this.prevBtn = this.querySelector('.ng-collage-showcase__arrow--prev');
      this.nextBtn = this.querySelector('.ng-collage-showcase__arrow--next');
      this.dotsContainer = this.querySelector('.ng-collage-showcase__dots');
      this.progressBar = this.querySelector('.ng-collage-showcase__progress-bar');
    }

    /* ─── LAYOUT CALCULATION ─── */

    _calculateLayout() {
      const isDesktop = window.matchMedia('(min-width: 750px)').matches;
      this._isDesktop = isDesktop;

      if (this.cards.length === 0) return;

      // Card step = card width + gap
      const trackStyle = getComputedStyle(this.track);
      this.gap = parseFloat(trackStyle.gap) || 16;
      this.cardStep = this.cards[0].offsetWidth + this.gap;

      // Viewport width
      this.viewportWidth = this.viewport ? this.viewport.offsetWidth : 0;

      // Total track width
      const trackWidth = this.cards.length * this.cardStep - this.gap;

      // Translate bounds
      this.maxTranslate = 0;
      this.minTranslate = -(trackWidth - this.viewportWidth);
      if (this.minTranslate > 0) this.minTranslate = 0;

      // Max index (each card can be active)
      this.maxIndex = Math.max(0, this.cards.length - 1);

      // Should sticky be active for this breakpoint?
      this._shouldSticky = (isDesktop ? this.enableStickyDesktop : this.enableStickyMobile)
        && !this._reducedMotion.matches;

      // Apply or remove sticky height on the root section
      if (this._shouldSticky) {
        this._applyStickyHeight();
      } else {
        this._removeStickyHeight();
      }

      // Calculate sticky scroll positions
      this._calculateStickyPositions();

      // Apply mobile sticky class
      if (this.enableStickyMobile && !isDesktop && !this._reducedMotion.matches) {
        this.classList.add('ng-collage-showcase--sticky-enabled-mobile');
      } else {
        this.classList.remove('ng-collage-showcase--sticky-enabled-mobile');
      }

      // Clamp
      if (this.currentIndex > this.maxIndex) this.currentIndex = this.maxIndex;
      if (this.currentIndex < 0) this.currentIndex = 0;
    }

    _applyStickyHeight() {
      // The root section needs height = 100svh + scrollDistance
      // This creates scroll room while the pin-wrapper is stuck
      const viewportHeight = window.innerHeight;
      this.scrollDistance = viewportHeight * this.scrollMultiplier;

      // Temporarily reset to measure natural height
      this.style.height = '';

      // Force the pin-wrapper to its natural (non-sticky) height temporarily
      const pinWrapperStyle = this.pinWrapper.style.cssText;
      this.pinWrapper.style.position = 'relative';
      this.pinWrapper.style.height = 'auto';

      const naturalHeight = this.pinWrapper.offsetHeight;

      // Restore pin-wrapper
      this.pinWrapper.style.cssText = pinWrapperStyle;

      // Set root height: viewport height + scroll distance
      // This gives enough room for the sticky pin to work through the full scroll range
      const totalHeight = viewportHeight + this.scrollDistance;
      this.style.height = totalHeight + 'px';
    }

    _removeStickyHeight() {
      this.style.height = '';
      this.scrollDistance = 0;
    }

    _calculateStickyPositions() {
      if (!this._shouldSticky) {
        this.stickyStart = 0;
        this.stickyEnd = 0;
        return;
      }

      // Use offsetTop for a stable reference (doesn't change with scroll)
      this.stickyStart = this.offsetTop;
      this.stickyEnd = this.stickyStart + this.scrollDistance;
    }

    /* ─── EVENTS ─── */

    _bindEvents() {
      // Scroll — main driver for sticky behavior
      window.addEventListener('scroll', this._onScroll, { passive: true });

      // Arrows
      if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrevClick);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNextClick);

      // Keyboard
      this.addEventListener('keydown', this._onKeyDown);

      // Drag / Swipe — only for non-sticky or mobile
      if (this.track) {
        this.track.addEventListener('touchstart', this._onDragStart, { passive: true });
        this.track.addEventListener('mousedown', this._onDragStart);
      }
      window.addEventListener('touchmove', this._onDragMove, { passive: false });
      window.addEventListener('touchend', this._onDragEnd);
      window.addEventListener('mousemove', this._onDragMove);
      window.addEventListener('mouseup', this._onDragEnd);

      // Prevent link clicks during drag
      this._clickGuard = (e) => {
        if (this.wasDragged) {
          e.preventDefault();
          e.stopPropagation();
          this.wasDragged = false;
        }
      };
      this.addEventListener('click', this._clickGuard, true);

      // Resize
      window.addEventListener('resize', this._onResize);

      // ResizeObserver
      if (window.ResizeObserver && this.viewport) {
        this._resizeObserver = new ResizeObserver(() => {
          this._calculateLayout();
          this._updateCardStates(this.currentIndex);
        });
        this._resizeObserver.observe(this.viewport);
      }

      // Reduced motion
      this._reducedMotion.addEventListener('change', this._onMotionChange);
    }

    _unbindEvents() {
      window.removeEventListener('scroll', this._onScroll, { passive: true });
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this._onPrevClick);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this._onNextClick);
      this.removeEventListener('keydown', this._onKeyDown);

      if (this.track) {
        this.track.removeEventListener('touchstart', this._onDragStart);
        this.track.removeEventListener('mousedown', this._onDragStart);
      }
      window.removeEventListener('touchmove', this._onDragMove);
      window.removeEventListener('touchend', this._onDragEnd);
      window.removeEventListener('mousemove', this._onDragMove);
      window.removeEventListener('mouseup', this._onDragEnd);

      if (this._clickGuard) {
        this.removeEventListener('click', this._clickGuard, true);
      }

      window.removeEventListener('resize', this._onResize);
      this._reducedMotion.removeEventListener('change', this._onMotionChange);
    }

    /* ─── SCROLL HANDLER ───
       This is the core of the scroll-driven product slider.
       Uses offsetTop for stable position reference. */

    _onScroll() {
      if (!this._shouldSticky) return;

      const scrollY = window.scrollY;
      const wasInRange = this.isInStickyRange;

      // Calculate progress using stable offsetTop
      if (scrollY >= this.stickyStart && scrollY <= this.stickyEnd) {
        this.isInStickyRange = true;
        this.progress = (scrollY - this.stickyStart) / this.scrollDistance;

        // Clamp to 0..1
        this.progress = Math.max(0, Math.min(1, this.progress));

        // Map progress to track translate
        this.targetTranslate = this.minTranslate * this.progress;

        // Map progress to active index
        const activeIndex = Math.round(this.progress * this.maxIndex);
        if (activeIndex !== this.currentIndex) {
          this.currentIndex = activeIndex;
          this._updateCardStates(this.currentIndex);
          this._updateDots();
          this._updateProgressBar();
          this._updateArrowStates();
        }

        // Image parallax
        if (this.parallaxStrength > 0 && this.imageEl) {
          const scale = 1 + (this.parallaxStrength / 100) * 0.08;
          const shift = this.progress * this.parallaxStrength * 0.4;
          this.imageEl.style.transform = `scale(${scale}) translateY(${shift}px)`;
        }

      } else if (scrollY > this.stickyEnd) {
        // Past sticky range — show last product
        this.isInStickyRange = false;
        if (wasInRange || this.currentIndex !== this.maxIndex) {
          this.progress = 1;
          this.targetTranslate = this.minTranslate;
          this.currentIndex = this.maxIndex;
          this._updateCardStates(this.maxIndex);
          this._updateDots();
          this._updateProgressBar();
          this._updateArrowStates();
        }

      } else {
        // Before sticky range — show first product
        this.isInStickyRange = false;
        if (wasInRange || this.currentIndex !== 0) {
          this.progress = 0;
          this.targetTranslate = 0;
          this.currentIndex = 0;
          this._updateCardStates(0);
          this._updateDots();
          this._updateProgressBar();
          this._updateArrowStates();
        }
      }
    }

    /* ─── DRAG / SWIPE ─── */

    _getX(e) {
      if (e.touches && e.touches.length > 0) return e.touches[0].clientX;
      return e.clientX;
    }

    _onDragStart(e) {
      // Don't drag on buttons/links/quick-add
      if (e.target.closest('button, a, .quick-add, product-form, form, input, select')) return;

      // On desktop with sticky active, don't drag (scroll drives the slider)
      if (this._shouldSticky && this._isDesktop) return;

      this.isDragging = true;
      this.wasDragged = false;
      this.startX = this._getX(e);
      this.prevTranslate = this.currentTranslate;
      this.lastX = this.startX;
      this.lastTime = Date.now();
      this.velocityX = 0;

      if (this.track) {
        this.track.classList.remove('ng-collage-showcase__track--animating');
      }
    }

    _onDragMove(e) {
      if (!this.isDragging) return;

      const x = this._getX(e);
      const diff = x - this.startX;
      const now = Date.now();

      // Velocity
      const dt = now - this.lastTime;
      if (dt > 0) {
        const instantVelocity = (x - this.lastX) / dt;
        this.velocityX = 0.7 * instantVelocity + 0.3 * this.velocityX;
      }
      this.lastX = x;
      this.lastTime = now;

      if (Math.abs(diff) > 5) this.wasDragged = true;

      let newTranslate = this.prevTranslate + diff;

      // Rubber-band
      if (newTranslate > this.maxTranslate) {
        newTranslate = this.maxTranslate + (newTranslate - this.maxTranslate) * 0.2;
      } else if (newTranslate < this.minTranslate) {
        newTranslate = this.minTranslate - (this.minTranslate - newTranslate) * 0.2;
      }

      this.targetTranslate = newTranslate;

      // Update active index
      const ratio = Math.abs(this.targetTranslate) / (Math.abs(this.minTranslate) || 1);
      const newIndex = Math.round(ratio * this.maxIndex);
      if (newIndex !== this.currentIndex && newIndex >= 0 && newIndex <= this.maxIndex) {
        this.currentIndex = newIndex;
        this._updateCardStates(this.currentIndex);
        this._updateDots();
        this._updateProgressBar();
        this._updateArrowStates();
      }
    }

    _onDragEnd() {
      if (!this.isDragging) return;
      this.isDragging = false;

      const movedBy = this.currentTranslate - this.prevTranslate;
      const velocity = this.velocityX;
      const flickThreshold = 0.15;

      let targetIndex = this.currentIndex;

      if (Math.abs(velocity) > flickThreshold) {
        const momentum = velocity * 150;
        const projected = this.currentTranslate + momentum;
        targetIndex = Math.round(Math.abs(projected) / this.cardStep);
      } else if (Math.abs(movedBy) > this.cardStep * 0.12) {
        targetIndex = movedBy < 0 ? this.currentIndex + 1 : this.currentIndex - 1;
      } else {
        targetIndex = Math.round(Math.abs(this.currentTranslate) / this.cardStep);
      }

      this.goToIndex(targetIndex, true);
    }

    /* ─── NAVIGATION ─── */

    _onPrevClick(e) {
      e.preventDefault();
      this.goToIndex(this.currentIndex - 1, true);
    }

    _onNextClick(e) {
      e.preventDefault();
      this.goToIndex(this.currentIndex + 1, true);
    }

    _onKeyDown(e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.goToIndex(this.currentIndex - 1, true);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.goToIndex(this.currentIndex + 1, true);
      }
    }

    goToIndex(index, animate) {
      if (index < 0) index = 0;
      if (index > this.maxIndex) index = this.maxIndex;

      this.currentIndex = index;

      const target = -index * this.cardStep;
      this.targetTranslate = Math.max(this.minTranslate, Math.min(this.maxTranslate, target));

      if (animate && this.track) {
        this.track.classList.add('ng-collage-showcase__track--animating');
        clearTimeout(this._animTimeout);
        this._animTimeout = setTimeout(() => {
          if (this.track) this.track.classList.remove('ng-collage-showcase__track--animating');
        }, 550);
      }

      this._updateCardStates(this.currentIndex);
      this._updateDots();
      this._updateProgressBar();
      this._updateArrowStates();
    }

    /* ─── ANIMATION LOOP ─── */

    _animationLoop() {
      // Smooth interpolation
      const diff = this.targetTranslate - this.currentTranslate;
      if (Math.abs(diff) > 0.3) {
        this.currentTranslate += diff * this.smoothing;
      } else {
        this.currentTranslate = this.targetTranslate;
      }

      // Apply transform
      if (this.track) {
        this.track.style.transform = `translate3d(${this.currentTranslate}px, 0, 0)`;
      }

      this._rafId = requestAnimationFrame(this._animationLoop);
    }

    /* ─── CARD STATES ─── */

    _updateCardStates(activeIndex) {
      if (!this.enableFade || this._reducedMotion.matches) return;

      this.cards.forEach((card, i) => {
        const isActive = i === activeIndex;
        card.classList.toggle('ng-collage-showcase__card--active', isActive);

        if (isActive) {
          card.style.opacity = this.activeOpacity;
          card.style.transform = `scale(${this.activeScale})`;
        } else {
          // Distance-based graceful fade
          const distance = Math.abs(i - activeIndex);
          const fadeStep = (this.activeOpacity - this.inactiveOpacity) * 0.5;
          const opacity = Math.max(this.inactiveOpacity,
            this.activeOpacity - distance * fadeStep);
          card.style.opacity = opacity;

          // Scale: slightly smaller for adjacent, more for distant
          const scaleStep = (this.activeScale - this.inactiveScale) * 0.4;
          const scale = Math.max(this.inactiveScale,
            this.activeScale - distance * scaleStep);
          card.style.transform = `scale(${scale})`;
        }
      });
    }

    /* ─── DOTS ─── */

    _createDots() {
      if (!this.dotsContainer) return;
      this.dotsContainer.innerHTML = '';

      for (let i = 0; i < this.cards.length; i++) {
        const dot = document.createElement('button');
        dot.classList.add('ng-collage-showcase__dot');
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', `Product ${i + 1} of ${this.cards.length}`);
        dot.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        dot.addEventListener('click', () => this.goToIndex(i, true));
        this.dotsContainer.appendChild(dot);
      }

      this.dots = Array.from(this.dotsContainer.querySelectorAll('.ng-collage-showcase__dot'));
      this._updateDots();
    }

    _updateDots() {
      if (!this.dots || !this.dots.length) return;
      this.dots.forEach((dot, i) => {
        const isActive = i === this.currentIndex;
        dot.classList.toggle('ng-collage-showcase__dot--active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    /* ─── PROGRESS BAR ─── */

    _updateProgressBar() {
      if (!this.progressBar) return;
      const pct = this.maxIndex > 0
        ? (this.currentIndex / this.maxIndex) * 100
        : 0;
      this.progressBar.style.width = pct + '%';
    }

    /* ─── ARROW STATES ─── */

    _updateArrowStates() {
      if (this.prevBtn) this.prevBtn.disabled = this.currentIndex <= 0;
      if (this.nextBtn) this.nextBtn.disabled = this.currentIndex >= this.maxIndex;
    }

    /* ─── INTRO ANIMATION ─── */

    _setupIntro() {
      if (!this.imageWrapper) return;
      this.imageWrapper.classList.add('ng-collage-showcase__image-wrapper--intro');

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            requestAnimationFrame(() => {
              this.imageWrapper.classList.add('ng-collage-showcase__image-wrapper--intro-visible');
            });
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.15 });

      observer.observe(this.imageWrapper);
    }

    /* ─── RESIZE ─── */

    _onResize() {
      this._calculateLayout();
      this._updateCardStates(this.currentIndex);
      this._createDots();
      this._updateProgressBar();
      this._updateArrowStates();
    }

    /* ─── MOTION PREFERENCE ─── */

    _onMotionChange() {
      if (this._reducedMotion.matches) {
        this.classList.remove('ng-collage-showcase--sticky-enabled-mobile');
        this._removeStickyHeight();
        this._updateCardStates(this.currentIndex);
      } else {
        this._calculateLayout();
      }
    }

    /* ─── SHOPIFY THEME EDITOR ─── */

    _onSectionLoad(e) {
      if (e.detail && e.detail.sectionId === this.sectionId) {
        this._readConfig();
        this._cacheDOM();
        if (this.track && this.cards.length > 0) {
          this._calculateLayout();
          this._createDots();
          this._updateCardStates(this.currentIndex);
          this._updateArrowStates();
        }
      }
    }

    /* ─── UTILITY ─── */

    _debounce(fn, ms) {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), ms);
      };
    }
  }

  customElements.define('next-gen-collage-showcase', NextGenCollageShowcase);
}
